<template lang="pug">
    .main-container
        ManagementPage.scrollable(:title="$t(`${baseTranslationPath}.name`)"
                  @fetchData="loadList"
                  :searchPageSize="searchPageSize",
                  @openEditor="openEditor"
                  :showTotals="true"
                  :teamFilter="false"
                  :creatable="canView('/iqtools/forms/new')"
                  :creationTooltipContent="$t(`${baseTranslationPath}.add_form`)"
                  :pagination="pagination"
                  ref="managementPage")
          router-view(v-slot:editor)
          template(v-slot:body)
            aiq-table.manage-documents-scrollbar(:data="items" v-loading="loading")
              aiq-table-column(:label="$t(`${baseTranslationPath}.table_name`)"
                              label-class-name="is-bordered"
                              min-width="15"
                              :sortable="true"
                              )
                template(v-slot:default="scope")
                   span {{ scope.row.title }}
              aiq-table-column(:label="$t(`${baseTranslationPath}.form_status`)"
                              label-class-name="is-bordered"
                              min-width="10"
                              :sortable="true"
                              )
                template(v-slot:default="scope")
                   span.status-pill(:class="statusClass(scope)") {{ getStatus(scope) }}
              aiq-table-column(:label="$t(`${baseTranslationPath}.questions`)"
                              label-class-name="is-bordered"
                              min-width="10"
                              :sortable="true"
                              )
                template(v-slot:default="scope")
                   span {{ getQuestionCount(scope) }}
              aiq-table-column(:label="$t(`${baseTranslationPath}.last_updated`)"
                              label-class-name="is-bordered"
                              min-width="10"
                              :sortable="true"
                              )
                template(v-slot:default="scope")
                   span {{ getCreatedAt(scope) }}
              aiq-table-column(label-class-name="is-bordered" min-width="10")
                template(v-slot:default="scope")
                   .row-actions
                     aiq-button(v-if="canView('/iqtools/forms/:number')"
                     type="text" size="small", @click="openEditor(scope.row)") {{ $t(`${baseTranslationPath}.manage`) }}
</template>
<script>
import get from 'lodash/get';
import dayjs from '@/localPlugins/dayjs';
import { mapState } from 'vuex';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { PAGINATION_DEFAULT } from '@/constants/pagination';
import { STATUS } from './fieldUtil';

const SEARCH_PAGE_SIZE = 25;

export default {
  name: 'Forms',
  components: {
    ManagementPage,
  },
  data() {
    return {
      searchPageSize: SEARCH_PAGE_SIZE,
      pagination: PAGINATION_DEFAULT,
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
    };
  },
  computed: {
    ...mapState({
      items: state => state.agentForms.agentForms,
    }),
  },
  methods: {
    async loadList({ searchTerm, searchOffset } = {}) {
      const response = await this.$store.dispatch(
        'agentForms/listForms',
        {
          limit: this.searchPageSize,
          offset: searchOffset,
          q: searchTerm,
        },
      );
      this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      return response;
    },

    getQuestionCount(scope) {
      return get(scope.row, 'question_count', 0);
    },

    // Status reflects processing first, then the publish lifecycle. While the
    // extract/match workflows are still running the form isn't manageable yet,
    // so show Extracting/Matching; once both complete, show Draft/Published.
    statusKind(scope) {
      const extractStatus = get(scope.row, 'extract_status');
      const matchStatus = get(scope.row, 'match_status');
      if (extractStatus === STATUS.failed || matchStatus === STATUS.failed) {
        return 'failed';
      }
      if (extractStatus !== STATUS.completed) {
        return 'extracting';
      }
      if (matchStatus !== STATUS.completed) {
        return 'matching';
      }
      return get(scope.row, 'draft', true) ? 'draft' : 'published';
    },
    getStatus(scope) {
      return this.$t(`${this.baseTranslationPath}.status_${this.statusKind(scope)}`);
    },
    statusClass(scope) {
      const kind = this.statusKind(scope);
      if (kind === 'extracting' || kind === 'matching') {
        return 'is-processing';
      }
      return `is-${kind}`;
    },
    getCreatedAt(scope) {
      const date = get(scope.row, 'created_at', '');
      return dayjs(date).format('YYYY-MM-DD HH:mm A');
    },
    openEditor(record = {}) {
      const id = get(record, 'id', 'new');
      if (id !== 'new') {
        const status = get(record, 'match_status');
        if (status !== STATUS.completed) {
          this.$aiq.notify.info(this.$t(`${this.baseTranslationPath}.navigate_error`));
          return;
        }
      }
      const path = `${this.$route.path}/${id}`;
      this.$router.push({
        path,
      });

    },
  },
};
</script><style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.row-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 6px;
    background-color: currentColor;
  }

  &.is-draft {
    color: $aiq-color-warning;
    background-color: rgba($aiq-color-warning, 0.12);
  }

  &.is-published {
    color: #3fb950;
    background-color: rgba(63, 185, 80, 0.12);
  }

  &.is-processing {
    color: #4872b1;
    background-color: rgba(72, 114, 177, 0.12);
  }

  &.is-failed {
    color: #f85149;
    background-color: rgba(248, 81, 73, 0.12);
  }
}

// Match the Manage (FormPage) heading weight so "Forms" reads identically across
// the list and manage views. Scoped to this page only (global ManagementPage
// titles stay bold 700). Higher specificity than common.scss .content-inner .header .title.
:deep(.content-inner .header .title) {
  font-weight: 400;
}
</style>
